import { ref } from 'vue';
import type { Channels } from '@eureka/shared/src/login-sdk';
import type { I18n } from 'vue-i18n';
import { initLoginSdk } from '@/utils/login-sdk-utils';
import { EMPOWER_TYPE, CHANNEL_SOURCE } from '@eureka/shared/src/login-sdk';
import { i18n } from '@/i18n';

/**
 * 登录 SDK Composable
 * 封装登录状态管理，提供响应式的 isLoggingIn 状态
 *
 * @param i18n - i18n 实例
 * @param channels - 渠道参数
 * @returns 登录相关方法和状态
 */
export function useLoginSdk(_i18n?: I18n, _channels?: Channels) {
    const i18nInstance = _i18n || i18n; // 使用传入的 i18n 实例，或默认导入的实例
    const channels = _channels || {
        from_page: EMPOWER_TYPE.AIR_HOME,
        channel_source: CHANNEL_SOURCE.AIR_EUREKA,
    }; // 使用传入的渠道参数，或默认空对象

    // 响应式状态：是否正在登录流程中
    const isLoggingIn = ref(false);

    // 初始化登录 SDK（纯 TS 工具函数）
    const { openLoginDialog: _openLoginDialog } = initLoginSdk(i18nInstance, channels);

    /**
     * 打开登录对话框（带状态管理）
     * @param withoutClose - 是否禁用关闭按钮（默认 true）
     * @param customChannels - 自定义渠道参数（可选）
     * @returns Promise - 登录成功且应用重新初始化完成后 resolve
     */
    const openLoginDialog = async (withoutClose = true, customChannels?: Channels): Promise<void> => {
        // 如果已经在登录流程中，避免重复打开
        if (isLoggingIn.value) {
            return;
        }

        try {
            // 标记开始登录
            isLoggingIn.value = true;

            // 调用底层登录方法
            await _openLoginDialog(withoutClose, customChannels);
        } finally {
            // 无论成功或失败，都重置状态
            isLoggingIn.value = false;
        }
    };

    return {
        /** 是否正在登录流程中（包括弹窗显示和 reinit 过程） */
        isLoggingIn,
        /** 打开登录对话框 */
        openLoginDialog,
    };
}
